import React, { FunctionComponent, useState, useEffect, useRef, useCallback } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { connect } from 'react-redux';
import { any, map } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { parse } from 'query-string';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Pages, getDefaultPage, ReferenceItem, RefFormPages, getDefaultRefFormPage } from './utils';
import IntroPage from '../../components/IntroPage';
import ConsentWaiver from '../../components/ConsentWaiver';
import {
  confirmPrivacyPolicyRoutine,
  fetchRefRequestRoutine,
  addPhoneNumberRoutine,
  saveReferencesRoutine,
  checkCodeRoutine,
  editReferenceRoutine,
  setEditReferenceRoutine,
  checkKeyRoutine
} from '../../routines';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import PersonalPhone from '../../components/PersonalPhone';
import { IContactPhoneNumber } from '../../../../common/models/candidate/ICandidatePhoneNumber';
import ThankYouScreen from '../../components/ThankYouScreen';
import RefProgressPage from '../RefProgressPage';
import FinalizePage from '../FinalizePage';
import ReferenceForm from '../ReferenceForm';
import PreviewEmail from '../PreviewEmail';
import CheckCode from '../../../../components/CheckCode';
import Sidebar from '../../../../components/CandidateFormSidebar';
import { getReferenceProgress } from '../../../../components/ReferenceRoleForm/utils';
import EditReferenceForm from '../EditReferenceForm';
import { CandidateFormState } from '../../reducers';
import { usePrevious } from '../../../../common/helpers/customHooks';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import { IReferenceCandidateFormMessage } from '../../../../common/models/reference/IReferenceCandidateFormMessage';
import RefFormSidebar from '../../../../components/RefFormSidebar';
import { WorkspaceState } from '../../../../reducers/workspace';
import styles from './styles.module.scss';
import { setWorkspaceColorsRoutine } from '../../../../routines/workspace';
import { getBackgroundURL } from '../../../../common/helpers/backgroundHelper';

interface ICandidateFormProps extends RouteComponentProps {
  referenceRequest: CandidateFormState;
  error: string | null;
  workspace: WorkspaceState;
  fetchRefReqR: IBindingAction;
  confirmPrivacyPolicyR: IBindingAction;
  addPhoneNumberR: IBindingCallback1<IContactPhoneNumber>;
  saveReferencesR: IBindingCallback1<IReferenceCandidateFormMessage>;
  onEditReference: IBindingCallback1<IReferenceCandidateForm>;
  setEditReference: IBindingCallback1<IReferenceCandidateForm>;
  checkCodeR: IBindingCallback1<string>;
  setWorkspaceColors: IBindingCallback1<boolean>;
  checkKey: IBindingAction;
}

const CandidateForm: FunctionComponent<ICandidateFormProps> = ({
  referenceRequest,
  workspace,
  location,
  history,
  fetchRefReqR,
  confirmPrivacyPolicyR,
  addPhoneNumberR,
  saveReferencesR,
  onEditReference,
  setEditReference,
  setWorkspaceColors,
  checkCodeR,
  checkKey
}) => {
  const queryParams = parse(location.search);
  if (!queryParams.key) {
    history.push('/');
    return null;
  }

  const [newReferences, setNewReferences] = useState<IReferenceCandidateForm[]>([]);
  const [currentPage, setCurrentPage] = useState<Pages | null>(null);
  const [currentRefPage, setCurrentRefPage] = useState(RefFormPages.Overview);
  const [animationClass, setAnimationClass] = useState<'left' | 'right'>('left');
  const prevRenderedPage = usePrevious(currentPage as Pages);
  const [prevPage, setPrevPage] = useState<Pages | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  const onChangePage = (page: Pages, goBack?: boolean) => {
    setAnimationClass(goBack ? 'right' : 'left');
    setCurrentPage(page);
  };

  const onChangeRefFormPage = (page: RefFormPages, goBack?: boolean) => {
    setAnimationClass(goBack ? 'right' : 'left');
    setCurrentRefPage(page);
  };

  useEffect(() => {
    if (!queryParams.code && queryParams.key) {
      checkKey();
    }
    if (queryParams.code && !referenceRequest.candidateId) {
      fetchRefReqR();
    }

    setWorkspaceColors(true);
    return () => setWorkspaceColors(false);
  }, []);

  useEffect(() => {
    if (referenceRequest.error) {
      history.push('/');
    }
  }, [referenceRequest.error]);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = 0;
    }

    setPrevPage(prevRenderedPage as Pages);

    if (currentPage === Pages.ReferenceForm) {
      setCurrentRefPage(getDefaultRefFormPage(referenceRequest.references));
    }
  }, [currentPage]);

  useEffect(() => {
    if (!queryParams.code && !referenceRequest.candidateId) onChangePage(Pages.CheckCode);
  }, [queryParams.code]);

  useEffect(() => {
    if (referenceRequest.candidateId) onChangePage(getDefaultPage(referenceRequest));
  }, [referenceRequest.candidateId]);

  const confirmPrivacyPolicy = () => {
    if (!referenceRequest.privacyPolicyAgreed) {
      confirmPrivacyPolicyR();
    }
    onChangePage(Pages.PhoneNumber);
  };

  const onPhoneNumberNextPage = () => (
    onChangePage(referenceRequest.references.length ? Pages.RefProgressPage : Pages.ReferenceForm)
  );

  const addPhoneNumber = (phoneNumber: string) => {
    addPhoneNumberR({ id: referenceRequest.contactId, phoneNumber });
    onPhoneNumberNextPage();
  };

  const finalize = (references: IReferenceCandidateForm[]) => {
    if (references.length) {
      setNewReferences(references);
      onChangePage(Pages.PreviewEmail);
    } else {
      onChangePage(Pages.RefProgressPage);
    }
  };

  const saveReferences = (message: string, sendSMS: boolean) => {
    const references = map(
      ({ id, ...rest }) => ({ id: id?.length === 36 ? id : undefined, ...rest }),
      newReferences
    );
    saveReferencesR({ message,
      sendSMS,
      candidateId: referenceRequest.candidateId,
      surveyId: referenceRequest.surveyId,
      references });
    onChangePage(Pages.ThankYouScreen);
    setNewReferences([]);
  };

  const showSidebar = [
    Pages.FinalizePage,
    Pages.PreviewEmail,
    Pages.RefProgressPage,
    Pages.EditReference,
    Pages.ReferenceForm
  ].includes(currentPage as Pages);

  const goToPrevRefFormPage = () => {
    switch (currentRefPage) {
      case RefFormPages.Overview:
        onChangePage(Pages.PhoneNumber, true);
        break;
      case RefFormPages.ReferenceName:
        if (!referenceRequest.references.length) {
          onChangeRefFormPage(RefFormPages.Overview, true);
          return;
        }
        onChangeRefFormPage(RefFormPages.Final, true);
        break;
      case RefFormPages.ReferenceRole:
        onChangeRefFormPage(RefFormPages.ReferenceName, true);
        break;
      case RefFormPages.ReferenceContactInfo:
        onChangeRefFormPage(RefFormPages.ReferenceRole, true);
        break;
      case RefFormPages.ReferenceCommonCompany:
        onChangeRefFormPage(RefFormPages.ReferenceContactInfo, true);
        break;
      case RefFormPages.Final:
        if (referenceRequest.currentReference?.id) {
          onChangeRefFormPage(RefFormPages.ReferenceCommonCompany, true);
        } else {
          onChangePage(prevPage as Pages, true);
        }
        break;
      default:
        break;
    }
  };

  const goToPrevPage = () => {
    switch (currentPage) {
      case Pages.ConsentWaiver:
        onChangePage(Pages.Intro, true);
        break;
      case Pages.PhoneNumber:
        onChangePage(Pages.ConsentWaiver, true);
        break;
      case Pages.ReferenceForm:
        goToPrevRefFormPage();
        break;
      case Pages.FinalizePage:
        onChangePage(Pages.ReferenceForm);
        break;
      case Pages.PreviewEmail:
        onChangePage(Pages.FinalizePage, true);
        break;
      case Pages.EditReference:
        onChangePage(prevPage as Pages, true);
        setEditReference({} as IReferenceCandidateForm);
        break;
      default:
        break;
    }
  };

  const goBackHandler = useCallback((event: PopStateEvent) => {
    const isBack = !event.state;
    if (isBack) {
      history.goForward();
      goToPrevPage();
    }
  }, [currentPage, currentRefPage, prevPage]);

  useEffect(() => {
    window.addEventListener('popstate', goBackHandler);
    return () => {
      window.removeEventListener('popstate', goBackHandler);
    };
  }, [goBackHandler]);

  const containerWithSidebar = showSidebar || currentPage === Pages.ReferenceForm;
  const defaultSendSMS = any(({ phoneNumber }) => Boolean(phoneNumber), newReferences);

  const getCurrentPage = () => {
    switch (currentPage) {
      case Pages.CheckCode:
        return (
          <CheckCode
            isChecking={referenceRequest.isChecking}
            errorCode={referenceRequest.errorCode}
            onStart={checkCodeR}
          />
        );
      case Pages.Intro:
        return referenceRequest.candidateId ? (
          <IntroPage
            workspace={workspace}
            onStart={() => onChangePage(Pages.ConsentWaiver)}
            firstName={referenceRequest.firstName}
          />
        ) : null;
      case Pages.ConsentWaiver:
        return (
          <ConsentWaiver
            defaultChecked={referenceRequest.privacyPolicyAgreed}
            onGoBack={goToPrevPage}
            onContinue={confirmPrivacyPolicy}
          />
        );
      case Pages.PhoneNumber:
        return (
          <PersonalPhone
            onGoBack={goToPrevPage}
            onSkip={onPhoneNumberNextPage}
            onAddPhoneNumber={addPhoneNumber}
            phoneNumber={referenceRequest.phoneNumber}
          />
        );
      case referenceRequest.candidateId && Pages.ReferenceForm:
        return (
          <ReferenceForm
            logo={workspace.logo}
            onGoBack={goToPrevRefFormPage}
            onContinue={() => onChangePage(Pages.FinalizePage)}
            setCurrentPage={onChangeRefFormPage}
            currentPage={currentRefPage}
          />
        );
      case Pages.FinalizePage:
        return (
          <FinalizePage
            onAddMoreReferences={goToPrevPage}
            onPreviewEmail={finalize}
          />
        );
      case Pages.PreviewEmail:
        return (
          <PreviewEmail
            onFinalize={goToPrevPage}
            onSendEmail={saveReferences}
            defaultSendSMS={defaultSendSMS}
          />
        );
      case Pages.ThankYouScreen:
        return (
          <ThankYouScreen
            workspace={workspace}
            onContinue={() => onChangePage(Pages.RefProgressPage)}
          />
        );
      case referenceRequest.candidateId && Pages.RefProgressPage:
        return <RefProgressPage onAddMoreReferences={() => onChangePage(Pages.ReferenceForm)} />;
      case Pages.EditReference:
        return (
          <EditReferenceForm
            onCancel={goToPrevPage}
            onSaveChanges={reference => {
              onChangePage(Pages.FinalizePage);
              onEditReference(reference);
            }}
          />
        );
      default:
        return <></>;
    }
  };

  const SideBarContent = () => (
    <>
      <div className={styles.title}>
        <FormattedMessage id="card.references.title" defaultMessage="References" />
        <div className={styles.counters}>
          {referenceRequest.references.length}
          /
          {referenceRequest.refCounters.totalCount}
        </div>
      </div>
      {map(r => <ReferenceItem key={r.type} type={r.type} provided={r.provided} total={r.total} />,
        getReferenceProgress(referenceRequest.references, referenceRequest.refCounters))}
    </>
  );

  useEffect(() => {
    if (referenceRequest.updateReference?.id) {
      onChangePage(Pages.EditReference);
    }
  }, [referenceRequest.updateReference]);

  const circleColor = workspace.settings && workspace.settings['--circle-color'];

  return (
    <div
      ref={ref}
      className="cf-bd"
      style={{ backgroundImage: getBackgroundURL(circleColor) }}
    >
      <TransitionGroup component={null}>
        <CSSTransition
          key={`${currentPage}.${currentRefPage}`}
          timeout={600}
          classNames={`slide-${animationClass}`}
        >
          <div className={`${styles.container} ${containerWithSidebar ? styles.containerWithSidebar : ''}`}>
            {getCurrentPage()}
          </div>
        </CSSTransition>
      </TransitionGroup>
      {showSidebar
        ? (
          <Sidebar
            circleColor={circleColor}
            logo={workspace.lightLogo as string}
          >
            {currentPage === Pages.ReferenceForm
              ? <RefFormSidebar currentPage={currentRefPage} {...referenceRequest} />
              : <SideBarContent />}
          </Sidebar>
        ) : <></>}
    </div>
  );
};

const mapStateToProps = ({
  referenceRequest,
  workspace
}: IAppState) => ({ referenceRequest, workspace });

const mapDispachToProps = {
  fetchRefReqR: fetchRefRequestRoutine,
  confirmPrivacyPolicyR: confirmPrivacyPolicyRoutine,
  addPhoneNumberR: addPhoneNumberRoutine,
  saveReferencesR: saveReferencesRoutine,
  checkCodeR: checkCodeRoutine,
  onEditReference: editReferenceRoutine,
  setEditReference: setEditReferenceRoutine,
  checkKey: checkKeyRoutine,
  setWorkspaceColors: setWorkspaceColorsRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(withRouter(CandidateForm));
